import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { CheckIcon, ChevronDownIcon, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface InfiniteScrollSelectOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

export interface InfiniteScrollSelectProps {
  options: InfiniteScrollSelectOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  className?: string
  triggerClassName?: string
  contentClassName?: string
  itemClassName?: string
  size?: 'sm' | 'default'
  searchable?: boolean
  searchPlaceholder?: string
  emptyMessage?: string
  loadingMessage?: string
  error?: string
  touched?: boolean
  label?: string
  labelClassName?: string
  isRequired?: boolean
  showDescriptionInSelected?: boolean
  showOptionDescriptionInSelected?: boolean
  onSearchChange?: (query: string) => void
  onBlur?: () => void
}

const InfiniteScrollSelect = ({
  options = [],
  value,
  onValueChange,
  placeholder = 'Select an option',
  disabled = false,
  loading = false,
  hasMore = false,
  onLoadMore,
  className,
  triggerClassName,
  contentClassName,
  itemClassName,
  size = 'default',
  searchable = false,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found',
  loadingMessage = 'Loading...',
  error,
  touched,
  label,
  labelClassName,
  isRequired = false,
  showDescriptionInSelected = true,
  showOptionDescriptionInSelected = true,
  onSearchChange,
  onBlur,
  ...props
}: InfiniteScrollSelectProps) => {
  const { ref: _ignoredRef, ...triggerProps } =
    props as InfiniteScrollSelectProps & {
      ref?: React.Ref<unknown>
    }
  const [searchQuery, setSearchQuery] = React.useState('')
  const [isOpen, setIsOpen] = React.useState(false)
  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const loadMoreLockRef = React.useRef(false)
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  // Memoize search handler to prevent re-renders
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
      onSearchChange?.(e.target.value)
    },
    [onSearchChange],
  )

  const normalizedSearchQuery = searchQuery.trim().toLowerCase()
  const shouldFilterLocally = !onSearchChange
  const filteredOptions = React.useMemo(() => {
    if (!shouldFilterLocally || !normalizedSearchQuery) return options
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(normalizedSearchQuery) ||
        option.description?.toLowerCase().includes(normalizedSearchQuery),
    )
  }, [options, normalizedSearchQuery, shouldFilterLocally])

  // Handle scroll to load more
  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10

      if (
        isNearBottom &&
        hasMore &&
        !isLoadingMore &&
        !loadMoreLockRef.current &&
        onLoadMore
      ) {
        loadMoreLockRef.current = true
        setIsLoadingMore(true)
        onLoadMore()
      }
    },
    [hasMore, isLoadingMore, onLoadMore],
  )

  // Reset loading state when hasMore changes
  React.useEffect(() => {
    if (!hasMore) {
      loadMoreLockRef.current = false
      setIsLoadingMore(false)
    }
  }, [hasMore])

  React.useEffect(() => {
    if (!loading) {
      loadMoreLockRef.current = false
      setIsLoadingMore(false)
    }
  }, [loading])

  // Focus search input when opened
  React.useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  // Clear search when closed
  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery((prev) => (prev ? '' : prev))
    }
  }, [isOpen])

  const selectedOption = options.find((option) => option.value === value)

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          className={`block text-xs font-medium text-gray-700 mb-1.5 ${labelClassName || ''}`}
        >
          {label}
          {isRequired && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      <SelectPrimitive.Root
        value={value}
        onValueChange={onValueChange}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open && onBlur) {
            onBlur()
          }
        }}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          className={cn(
            "border-input data-[placeholder]:text-muted-foreground cursor-pointer [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-10 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 max-w-full",
            error &&
              touched &&
              'border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500/20',
            triggerClassName,
          )}
          data-size={size}
          {...triggerProps}
        >
          <SelectPrimitive.Value placeholder={placeholder}>
            {selectedOption && (
              <div className='flex flex-col items-start'>
                <span className='font-medium'>{selectedOption.label}</span>
                {showDescriptionInSelected && selectedOption.description && (
                  <span className='text-xs text-gray-500'>
                    {selectedOption.description}
                  </span>
                )}
              </div>
            )}
          </SelectPrimitive.Value>
          <SelectPrimitive.Icon asChild>
            {loading ? (
              <Loader2 className='size-4 animate-spin' />
            ) : (
              <ChevronDownIcon className='size-4 opacity-50' />
            )}
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={cn(
              'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-80 w-[var(--radix-select-trigger-width)] origin-[--radix-select-content-transform-origin] overflow-hidden rounded-md border shadow-md',
              contentClassName,
            )}
            position='popper'
            sideOffset={4}
            align='start'
          >
            <SelectPrimitive.Viewport
              ref={viewportRef}
              className='w-full max-h-80 overflow-y-auto'
              onScroll={handleScroll}
            >
              {searchable && (
                <div className='w-full sticky top-0 bg-white border-b border-gray-200 p-2 z-10 -mt-1'>
                  <input
                    ref={searchInputRef}
                    type='text'
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className='w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-[#D4A574]'
                    autoComplete='off'
                  />
                </div>
              )}
              <div className='p-1'>
                {filteredOptions.length === 0 ? (
                  <div className='px-2 py-3 text-sm text-gray-500 text-center'>
                    {loading ? loadingMessage : emptyMessage}
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <SelectPrimitive.Item
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      className={cn(
                        "focus:bg-accent focus:text-accent-foreground cursor-pointer [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full items-center gap-2 rounded-sm py-2 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                        itemClassName,
                      )}
                    >
                      <span className='absolute right-2 flex size-3.5 items-center justify-center'>
                        <SelectPrimitive.ItemIndicator>
                          <CheckIcon className='size-4' />
                        </SelectPrimitive.ItemIndicator>
                      </span>
                      <div className='flex flex-col items-start flex-1 min-w-0'>
                        <span className='font-medium truncate'>
                          {option.label}
                        </span>
                        {showOptionDescriptionInSelected &&
                          option.description && (
                            <span className='text-xs text-gray-500 truncate'>
                              {option.description}
                            </span>
                          )}
                      </div>
                    </SelectPrimitive.Item>
                  ))
                )}

                {isLoadingMore && (
                  <div className='flex items-center justify-center py-2'>
                    <Loader2 className='size-4 animate-spin mr-2' />
                    <span className='text-sm text-gray-500'>
                      Loading more...
                    </span>
                  </div>
                )}
              </div>
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>

      {error && touched && (
        <span className='text-xs text-red-500 mt-1 block'>{error}</span>
      )}
    </div>
  )
}

InfiniteScrollSelect.displayName = 'InfiniteScrollSelect'

export { InfiniteScrollSelect }
